import { memo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "primereact/card";
import { useAppSelector } from "../../hooks/storeHooks";
import { selectInspectionData } from "../../store/selectors/inspectionSelectors";
import { selectDefects } from "../../store/selectors/defectSelectors";
import { selectPositiveItems } from "../../store/selectors/positiveItemSelectors";
import { formatInspectionDate } from "../../utils/inspectionHelper";
import { InspectionStatusTag } from "./InspectionStatusTag";

const Detail = ({ label, value, prominent, dataCy }: {
    label: string;
    value?: ReactNode;
    prominent?: boolean;
    dataCy: string;
}) => (
    <div className="flex gap-3">
        <span className="inspection-info__label">{label}</span>
        <span data-cy={dataCy} className={`inspection-info__value${prominent ? " inspection-info__value--prominent" : ""}`}>
            {value || "—"}
        </span>
    </div>
);

type InspectionInfoCardProps = {
    /** What the page-specific third column shows alongside the reference. */
    thirdColumn: "status" | "defectTotals";
};

/**
 * The standard inspection header card: vessel identity, checklist/location/date,
 * and a page-specific third column. Reads the inspection from the store; the
 * checklist allocation only exists on config-driven inspections, so that row
 * (like any missing value) falls back to "—".
 */
export const InspectionInfoCard = memo(({ thirdColumn }: InspectionInfoCardProps) => {
    const { t } = useTranslation("locale");
    const inspection = useAppSelector(selectInspectionData);
    const defects = useAppSelector(selectDefects);
    const positiveItems = useAppSelector(selectPositiveItems);

    if (!inspection) {
        return null;
    }

    const checklistType = inspection.checklistType as string | null | undefined;
    const checklistVersion = inspection.checklistVersion as string | null | undefined;
    const versionSuffix = checklistVersion ? `, v${checklistVersion}` : "";
    const checklistLabel = checklistType ? `${checklistType}${versionSuffix}` : null;
    const location = [inspection.port, inspection.country].filter(Boolean).join(", ");

    return (
        <Card>
            <div className="inspection-info">
                <div className="inspection-info__col flex-1 flex flex-column gap-2">
                    <Detail label={t("inspectionInfo.vesselName")} value={inspection.vesselName} prominent dataCy="info-vessel-name" />
                    <Detail label={t("inspectionInfo.imoNumber")} value={inspection.vesselImo} dataCy="info-imo-number" />
                    <Detail label={t("inspectionInfo.vesselType")} value={inspection.vesselType} dataCy="info-vessel-type" />
                </div>
                <div className="inspection-info__divider" />
                <div className="inspection-info__col flex-1 flex flex-column gap-2">
                    <Detail label={t("inspectionInfo.checklist")} value={checklistLabel} prominent dataCy="info-checklist" />
                    <Detail label={t("inspectionInfo.location")} value={location} dataCy="info-location" />
                    <Detail label={t("inspectionInfo.inspectionDate")} value={formatInspectionDate(inspection)} dataCy="info-inspection-date" />
                </div>
                <div className="inspection-info__divider" />
                <div className="inspection-info__col flex-1 flex flex-column gap-2">
                    <Detail label={t("inspectionInfo.reference")} value={inspection.inspectionNumber} prominent dataCy="info-reference" />
                    {thirdColumn === "status" && (
                        <Detail
                            label={t("inspectionInfo.status")}
                            value={inspection.status ? <InspectionStatusTag status={inspection.status} /> : null}
                            dataCy="info-status"
                        />
                    )}
                    {thirdColumn === "defectTotals" && (
                        <>
                            <Detail label={t("inspectionInfo.totalDefects")} value={String(defects.length)} dataCy="info-total-defects" />
                            <Detail label={t("inspectionInfo.totalPositiveItems")} value={String(positiveItems.length)} dataCy="info-total-positive-items" />
                        </>
                    )}
                </div>
            </div>
        </Card>
    );
});
